/**
 * Prefetch Utils - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Utility for prefetching news and events data and images when users first visit
 * the home page. This improves performance by loading the first few items in the
 * background so they're ready when users navigate to news/events pages.
 * 
 * Features:
 * - Prefetch first N news items and their images
 * - Prefetch first N events items and their images  
 * - Uses existing imageCache system for consistency
 * - Handles errors gracefully without blocking home page
 * - Configurable number of items to prefetch
 * - Deduplication to avoid duplicate requests
 * - Background processing that doesn't block UI
 * - Matches exact loading logic from MobileNews and MobileEvents
 * 
 * Usage:
 * import { prefetchNewsAndEvents } from './prefetchUtils';
 * 
 * // In home page component
 * useEffect(() => {
 *   prefetchNewsAndEvents(5); // Prefetch first 5 items
 * }, []);
 */

import apiHelper from './apiHelper';
import imageCache from './imageCache';
import { FILE_NAMES } from './constants';

/**
 * Configuration for prefetching
 */
const PREFETCH_CONFIG = {
  NEWS_ITEMS: 5,
  EVENTS_ITEMS: 5,
  MEDIA_ITEMS: 10, // Media centre has more variety, prefetch more
  TIMEOUT_MS: 30000 // 30 seconds timeout
};

/**
 * Get random fallback image for news (matches MobileNews logic)
 */
const getRandomNewsImage = () => {
  const randomIndex = Math.floor(Math.random() * 30) + 1;
  return `/assets/img/news/weekly/${randomIndex}.jpg`;
};

/**
 * Get random fallback image for events
 */
// const getRandomEventImage = () => {
//   const randomIndex = Math.floor(Math.random() * 30) + 1;
//   return `/assets/img/news/weekly/${randomIndex}.jpg`; // Using same as news for consistency
// };

/**
 * Get YouTube thumbnail URL
 */
const getYouTubeThumbnail = (youtubeUrl) => {
  let videoId = '';
  
  if (youtubeUrl.includes('youtube.com/watch?v=')) {
    videoId = youtubeUrl.split('v=')[1].split('&')[0];
  } else if (youtubeUrl.includes('youtu.be/')) {
    videoId = youtubeUrl.split('youtu.be/')[1].split('?')[0];
  }
  
  if (videoId) {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  }
  
  return null;
};

/**
 * Get Vimeo thumbnail URL using oEmbed API (modern approach)
 */
const getVimeoThumbnail = async (vimeoUrl) => {
  try {
    // Use oEmbed API instead of deprecated v2 API
    const oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}`;
    const response = await fetch(oembedUrl);
    
    // Check if the response is ok first
    if (!response.ok) {
      if (response.status === 404) {
        const videoId = vimeoUrl.split('vimeo.com/')[1]?.split('?')[0];
        console.warn(`Vimeo video ${videoId} not found (404). Video may have been deleted or made private.`);
      } else {
        console.warn(`Vimeo oEmbed API returned status ${response.status} for URL ${vimeoUrl}`);
      }
      return null;
    }
    
    const data = await response.json();
    
    // oEmbed returns thumbnail_url instead of thumbnail_large
    if (data && data.thumbnail_url) {
      return data.thumbnail_url;
    }
  } catch (error) {
    // Don't log JSON parsing errors as console.error since they're expected for 404s
    if (error instanceof SyntaxError) {
      console.warn('Vimeo oEmbed API returned non-JSON response, likely video not found');
    } else {
      console.warn('Error fetching Vimeo thumbnail via oEmbed:', error);
    }
  }
  
  return null;
};

/**
 * Prefetch news data and images
 * @param {number} limit - Number of news items to prefetch
 * @returns {Promise<void>}
 */
export const prefetchNews = async (limit = PREFETCH_CONFIG.NEWS_ITEMS) => {
  try {
    // Check if we're in login grace period - skip prefetch if so
    if (apiHelper.isInLoginGracePeriod && apiHelper.isInLoginGracePeriod()) {
      return;
    }

    // Fetch news data with retry for auth errors
    let newsResult;
    try {
      newsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MONTHLY_NEWS);
    } catch (error) {
      if (error.message === 'AUTH0_NOT_READY') {
       
        // Retry after a delay in background
        setTimeout(() => {
          prefetchNews(limit);
        }, 5000);
        return;
      }
      throw error;
    }

    const newsData = newsResult.fileContent || [];
    
    if (!Array.isArray(newsData) || newsData.length === 0) {
      return;
    }

    // Filter out intro articles and sort by portalPublishDate (same logic as MobileNews)
    const sortedNews = newsData
      .filter(item => item.weeklyCategory !== 'Intro')
      .sort((a, b) => {
        const dateA = new Date(a.portalPublishDate || '');
        const dateB = new Date(b.portalPublishDate || '');
        return dateB.getTime() - dateA.getTime(); // Newest first
      });

    // Take first N items
    const itemsToPrefetch = sortedNews.slice(0, limit);
  

    // Prefetch images for each news item
    const imagePromises = itemsToPrefetch.map(async (newsItem) => {
      if (!newsItem.id) return;
      
      // Use exact same key format as MobileNews
      const imageKey = `news-${newsItem.id}`;
      
      // Skip if already cached
      if (imageCache.getCachedImage(imageKey)) {
        return;
      }

      // Create the exact same loading function as MobileNews
      const loadFunction = async () => {
        if (newsItem.image) {
          try {
            if (newsItem.image.includes('https://')) {
              return newsItem.image;
            } else {
              const authToken = await apiHelper.getAuthToken();
              const apiImageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${newsItem.image}`;
              
              const response = await fetch(apiImageUrl, {
                headers: { 'Authorization': `Bearer ${authToken}` }
              });
              
              if (response.ok) {
                const blob = await response.blob();
                return URL.createObjectURL(blob);
              } else {
                throw new Error('Failed to fetch image');
              }
            }
          } catch (err) {
            return getRandomNewsImage();
          }
        } else {
          return getRandomNewsImage();
        }
      };

      try {
        await imageCache.getImage(imageKey, loadFunction);
      } catch (error) {
        // Silent fail for individual images
      }
    });

    // Wait for all images to load (with timeout)
    await Promise.allSettled(imagePromises);
    
  } catch (error) {
    console.warn('[Prefetch] News prefetch failed:', error);
  }
};

/**
 * Prefetch events data and images
 * @param {number} limit - Number of events items to prefetch
 * @returns {Promise<void>}
 */
export const prefetchEvents = async (limit = PREFETCH_CONFIG.EVENTS_ITEMS) => {
  try {
    // Check if we're in login grace period - skip prefetch if so
    if (apiHelper.isInLoginGracePeriod && apiHelper.isInLoginGracePeriod()) {
      return;
    }

    // Fetch events data with retry for auth errors
    let eventsResult;
    try {
      eventsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.EVENTS);
    } catch (error) {
      if (error.message === 'AUTH0_NOT_READY') {
        // Retry after a delay in background
        setTimeout(() => {
          prefetchEvents(limit);
        }, 6000); // Slightly different delay to avoid clustering
        return;
      }
      throw error;
    }

    const eventsData = eventsResult.fileContent || [];
    
    if (!Array.isArray(eventsData) || eventsData.length === 0) {
      return;
    }

    // Sort events by date (newest first)
    const sortedEvents = eventsData.sort((a, b) => {
      const dateA = new Date(a.EventDate || '');
      const dateB = new Date(b.EventDate || '');
      return dateB.getTime() - dateA.getTime();
    });

    // Take first N items
    const itemsToPrefetch = sortedEvents.slice(0, limit);
  

    // Prefetch images for each event item
    const imagePromises = itemsToPrefetch.map(async (event) => {
      if (!event.ID) return;
      
      // Skip if no image link
      if (!event.ImageLink) {
        return;
      }
      
      // Use exact same key format as MobileEvents
      const imageKey = `event-${event.ID}`;
      
      // Skip if already cached
      if (imageCache.getCachedImage(imageKey)) {
        return;
      }

      // Create the exact same loading function as MobileEvents
      const loadFunction = async () => {
        try {
          // Handle different image source types (same logic as MobileEvents)
          if (event.ImageLink.startsWith('blob:') || event.ImageLink.includes('https://') || 
              event.ImageLink.includes('assets/img/') || event.ImageLink.startsWith('/images/')) {
            return event.ImageLink;
          }

          // Load via API
          const authToken = await apiHelper.getAuthToken();
          const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${event.ImageLink}`;
          
          const response = await fetch(imageUrl, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });

          if (response.ok) {
            const blob = await response.blob();
            return URL.createObjectURL(blob);
          } else {
            throw new Error('Failed to fetch image');
          }
        } catch (error) {
          console.error('Error loading event image:', error);
          return '/images/news-reading.jpg';
        }
      };

      try {
        await imageCache.getImage(imageKey, loadFunction);
      } catch (error) {
        // Silent fail for individual images
      }
    });

    // Wait for all images to load (with timeout)
    await Promise.allSettled(imagePromises);
    
  } catch (error) {
    console.warn('[Prefetch] Events prefetch failed:', error);
  }
};

/**
 * Prefetch media centre images
 * @param {number} limit - Number of media items to prefetch
 * @returns {Promise<void>}
 */
export const prefetchMediaImages = async (limit = PREFETCH_CONFIG.MEDIA_ITEMS) => {
  try {
    // Check if we're in login grace period - skip prefetch if so
    if (apiHelper.isInLoginGracePeriod && apiHelper.isInLoginGracePeriod()) {
      return;
    }

    // Fetch media centre data with retry for auth errors
    let mediaResult;
    try {
      mediaResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MEDIA_CENTRE);
    } catch (error) {
      if (error.message === 'AUTH0_NOT_READY') {
        // Retry after a delay in background
        setTimeout(() => {
          prefetchMediaImages(limit);
        }, 7000); // Different delay to avoid clustering
        return;
      }
      throw error;
    }

    const mediaData = mediaResult.fileContent || [];
    
    if (!Array.isArray(mediaData) || mediaData.length === 0) {
      return;
    }

    // Filter for images only and sort by date
    const imageItems = mediaData
      .filter(item => item.type === 'image')
      .sort((a, b) => {
        const dateA = new Date(a.date || '');
        const dateB = new Date(b.date || '');
        return dateB.getTime() - dateA.getTime();
      });

    // Take first N items
    const itemsToPrefetch = imageItems.slice(0, limit);


    // Prefetch images
    const imagePromises = itemsToPrefetch.map(async (item) => {
      if (!item.webURLLink) return;
      
      const imageKey = `media-image-${item.webURLLink}`;
      
      // Skip if already cached
      if (imageCache.getCachedImage(imageKey)) {
        return;
      }

      // Create the exact same loading function as MediaCentre
      const loadImageFunction = async () => {
        // Check if it's a local file (not external URL)
        if (!item.webURLLink.includes('http://') && !item.webURLLink.includes('https://')) {
          try {
            // Build proper streaming URL
            const apiBaseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, '');
            const encodedFilename = encodeURIComponent(item.webURLLink);
            const mediaBaseUrl = apiBaseUrl.replace(/\/api$/, '');
            
            const streamingUrls = [
              `${mediaBaseUrl}/media/stream/${encodedFilename}`,
              `${apiBaseUrl}/media/stream/${encodedFilename}`
            ];
            
            for (const streamingUrl of streamingUrls) {
              try {
                const testResponse = await fetch(streamingUrl, { method: 'HEAD' });
                if (testResponse.ok) {
                  return streamingUrl;
                }
              } catch (streamError) {
                // Try next URL
              }
            }
            
            // Fallback to blob download
            const { fileContent } = await apiHelper.fetchImageByName(item.webURLLink);
            return URL.createObjectURL(fileContent);
          } catch (error) {
            throw new Error('Failed to load media image');
          }
        } else {
          // External URL - use directly
          return item.webURLLink;
        }
      };

      try {
        await imageCache.getImage(imageKey, loadImageFunction);
      } catch (error) {
        // Silent fail for individual images
      }
    });

    // Wait for all images to load
    await Promise.allSettled(imagePromises);
    
  } catch (error) {
    console.warn('[Prefetch] Media images prefetch failed:', error);
  }
};

/**
 * Prefetch media centre thumbnails
 * @param {number} limit - Number of media items to prefetch thumbnails for
 * @returns {Promise<void>}
 */
export const prefetchMediaThumbnails = async (limit = PREFETCH_CONFIG.MEDIA_ITEMS) => {
  try {
  
    // Fetch media data
    const mediaResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MEDIA_CENTRE);
    const mediaData = mediaResult.fileContent || [];
    
    if (!Array.isArray(mediaData) || mediaData.length === 0) {
  
      return;
    }

    // Filter for video and audio items that need thumbnails
    const mediaItems = mediaData.filter(item => 
      (item.mediaType && 
       (item.mediaType.toLowerCase() === 'video' || 
        item.mediaType.toLowerCase() === 'audio' || 
        item.mediaType === 'Audio')) && 
      item.webURLLink
    );



    // Sort by publishDate (newest first) - same as MediaCentre component
    const sortedMedia = mediaItems.sort((a, b) => {
      const dateA = new Date(a.publishDate || a.dateCreated || a.date || '');
      const dateB = new Date(b.publishDate || b.dateCreated || b.date || '');
      return dateB - dateA;
    });

    // Take first N items
    const itemsToPrefetch = sortedMedia.slice(0, limit);


    // Prefetch thumbnails
    const thumbnailPromises = itemsToPrefetch.map(async (item) => {
      if (!item.webURLLink) return;
      
      const thumbnailKey = `media-thumbnail-${item.webURLLink}`;
      
      // Skip if already cached
      if (imageCache.getCachedImage(thumbnailKey)) {
        return;
      }

      // Priority 1: Custom thumbnail (same logic as MediaCentre)
      if (item.thumbnail && item.thumbnail.trim() !== '' && item.thumbnail !== 'NONE') {
        const loadThumbnailFunction = async () => {
          if (!item.thumbnail.includes('http://') && !item.thumbnail.includes('https://')) {
            try {
              const apiBaseUrl = process.env.REACT_APP_API_URL.replace(/\/+$/, '');
              const encodedThumbnail = encodeURIComponent(item.thumbnail);
              const mediaBaseUrl = apiBaseUrl.replace(/\/api$/, '');
              
              const streamingUrls = [
                `${mediaBaseUrl}/media/stream/${encodedThumbnail}`,
                `${apiBaseUrl}/media/stream/${encodedThumbnail}`
              ];
              
              for (const streamingUrl of streamingUrls) {
                try {
                  const testResponse = await fetch(streamingUrl, { method: 'HEAD' });
                  if (testResponse.ok) {
                    return streamingUrl;
                  }
                } catch (streamError) {
                  // Try next URL
                }
              }
              
              // Fallback to blob download
              const { fileContent } = await apiHelper.fetchVideoByName(item.thumbnail);
              return URL.createObjectURL(fileContent);
            } catch (error) {
              throw new Error('Failed to fetch custom thumbnail');
            }
          } else {
            // External URL thumbnail
            return item.thumbnail;
          }
        };

        try {
          await imageCache.getImage(thumbnailKey, loadThumbnailFunction);
          return; // Successfully loaded custom thumbnail
        } catch (error) {
          // Continue to platform thumbnails
        }
      }

      // Priority 2: Platform thumbnails (YouTube/Vimeo)
      const isYouTubeOrVimeo = item.webURLLink.includes('vimeo.com') || 
                              item.webURLLink.includes('youtube.com') || 
                              item.webURLLink.includes('youtu.be');
      const shouldUsePlatformThumbnail = item.mediaType.toLowerCase() === 'video' && isYouTubeOrVimeo;
      
      if (shouldUsePlatformThumbnail) {
        const loadPlatformThumbnailFunction = async () => {
          let thumbnailUrl = null;
          
          if (item.webURLLink.includes('youtube.com') || item.webURLLink.includes('youtu.be')) {
            thumbnailUrl = getYouTubeThumbnail(item.webURLLink);
          } else if (item.webURLLink.includes('vimeo.com')) {
            thumbnailUrl = await getVimeoThumbnail(item.webURLLink);
          }
          
          if (!thumbnailUrl) {
            throw new Error('Failed to get platform thumbnail URL');
          }
          
          return thumbnailUrl;
        };

        try {
          await imageCache.getImage(thumbnailKey, loadPlatformThumbnailFunction);
        } catch (error) {
          // Silent fail for platform thumbnails
        }
      }
    });

    // Wait for all thumbnails to load
    await Promise.allSettled(thumbnailPromises);
    
  } catch (error) {
    console.warn('[Prefetch] Media thumbnails prefetch failed:', error);
  }
};

/**
 * Prefetch all media centre content (images and thumbnails)
 * @param {number} limit - Number of media items to prefetch
 * @returns {Promise<void>}
 */
export const prefetchMediaCentre = async (limit = PREFETCH_CONFIG.MEDIA_ITEMS) => {
  try {
  
    // Run both prefetch operations in parallel
    const results = await Promise.allSettled([
      prefetchMediaImages(limit),
      prefetchMediaThumbnails(limit)
    ]);
    
    const imageResult = results[0];
    const thumbnailResult = results[1];
    
    if (imageResult.status === 'fulfilled') {
      // continue
    } else {
      console.warn('[Prefetch] Media images prefetch failed:', imageResult.reason);
    }
    
    if (thumbnailResult.status === 'fulfilled') {
      // continue
    } else {
      console.warn('[Prefetch] Media thumbnails prefetch failed:', thumbnailResult.reason);
    }
  } catch (error) {
    console.warn('[Prefetch] Media centre prefetch failed:', error);
  }
};

/**
 * Prefetch both news and events data and images
 * @param {number} newsLimit - Number of news items to prefetch
 * @param {number} eventsLimit - Number of events items to prefetch
 * @returns {Promise<void>}
 */
export const prefetchNewsAndEvents = async (
  newsLimit = PREFETCH_CONFIG.NEWS_ITEMS,
  eventsLimit = PREFETCH_CONFIG.EVENTS_ITEMS
) => {
  try {
    // Run both prefetch operations in parallel
    await Promise.allSettled([
      prefetchNews(newsLimit),
      prefetchEvents(eventsLimit)
    ]);
  } catch (error) {
    console.warn('[Prefetch] News and events prefetch failed:', error);
  }
};

/**
 * Prefetch everything - news, events, and media centre
 * @param {number} newsLimit - Number of news items to prefetch
 * @param {number} eventsLimit - Number of events items to prefetch
 * @param {number} mediaLimit - Number of media items to prefetch
 * @returns {Promise<void>}
 */
export const prefetchAll = async (
  newsLimit = PREFETCH_CONFIG.NEWS_ITEMS,
  eventsLimit = PREFETCH_CONFIG.EVENTS_ITEMS,
  mediaLimit = PREFETCH_CONFIG.MEDIA_ITEMS
) => {
  try {
    // Check if we're in login grace period - skip all prefetch if so
    if (apiHelper.isInLoginGracePeriod && apiHelper.isInLoginGracePeriod()) {
      // Schedule for later
      setTimeout(() => {
        prefetchAll(newsLimit, eventsLimit, mediaLimit);
      }, 3000);
      return;
    }

    // Stagger the prefetch operations with delays to prevent simultaneous token expiration
   
    
    // Start with news immediately
    prefetchNews(newsLimit).catch(error => 
      console.warn('[Prefetch] News prefetch failed:', error?.message)
    );
    
    // Events after short delay
    setTimeout(() => {
      prefetchEvents(eventsLimit).catch(error =>
        console.warn('[Prefetch] Events prefetch failed:', error?.message)
      );
    }, 500);

    // Media centre after short delay
    setTimeout(() => {
      prefetchMediaCentre(mediaLimit).catch(error =>
        console.warn('[Prefetch] Media Centre prefetch failed:', error?.message)
      );
    }, 1000);
    
  } catch (error) {
    console.warn('[Prefetch] Full prefetch failed:', error);
  }
};

/**
 * Prefetch with timeout to prevent hanging
 * @param {Function} prefetchFunction - Function to run with timeout
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Promise<void>}
 */
export const prefetchWithTimeout = async (prefetchFunction, timeoutMs = PREFETCH_CONFIG.TIMEOUT_MS) => {
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(new Error('Prefetch timeout')), timeoutMs);
  });

  try {
    await Promise.race([prefetchFunction(), timeoutPromise]);
  } catch (error) {
    console.warn('[Prefetch] Operation timed out or failed:', error);
  }
}; 